import React, { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { StackScreenProps } from "@react-navigation/stack";
import { StyleSheet } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import { GET_WORKER_INFO } from "../../graphql/core";
import ListItem from "../../ui_atoms/ListItem";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import ScreenCard from "../ScreenCard";
import { TitleMediumText } from "../../components/FormattedText";
import { useTheme } from "../../hooks/use-theme";

type Props = StackScreenProps<ClientWorkersTabParamList, "WorkerQualifications">;

const WorkerQualificationsScreen: React.FC<Props> = ({ route }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { loading, error, data } = useQuery(GET_WORKER_INFO, {
        variables: { workerId: route?.params?.workerId || "" },
        fetchPolicy: "cache-and-network",
    });

    const workerInfo = useMemo(() => data?.getWorkerInfo, [data]);

    const { experiences = [], trainings = [] } = workerInfo?.curriculumVitae || {};

    return (
        <LoadingBoundary loading={loading && !data} error={error}>
            <ScreenCard>
                <VStack v="2/3" h="2/3" gap={1}>
                    <VStack gap="1/3">
                        <TitleMediumText>{i18n.t("worker_details.trainings")}</TitleMediumText>
                        {(trainings || []).length > 0 ? (
                            trainings?.map((training, index) => (
                                <ListItem
                                    key={index}
                                    style={styles.item}
                                    icon="school"
                                    title={i18n.t(`education_level.${training.educationLevel}`)}
                                    description={training.trainingName}
                                />
                            ))
                        ) : (
                            <ListItem
                                style={styles.item}
                                icon="school"
                                title={i18n.t(`worker_details.none`)}
                                description={i18n.t(`worker_details.no_trainings`)}
                            />
                        )}
                    </VStack>
                    <VStack gap="1/3">
                        <TitleMediumText>{i18n.t("worker_details.experiences")}</TitleMediumText>
                        {(experiences || []).length > 0 ? (
                            experiences?.map((experience, index) => (
                                <ListItem
                                    key={index}
                                    style={styles.item}
                                    icon="work"
                                    title={experience.professionName}
                                    description={i18n.t("worker_details.years_of_experience", {
                                        count: experience.yearsOfExperience,
                                    })}
                                />
                            ))
                        ) : (
                            <ListItem
                                style={styles.item}
                                icon="work"
                                title={i18n.t(`worker_details.none`)}
                                description={i18n.t(`worker_details.no_experiences`)}
                            />
                        )}
                    </VStack>
                </VStack>
            </ScreenCard>
        </LoadingBoundary>
    );
};

const useStyles = () => {
    const { marginSize } = useTheme();

    return StyleSheet.create({
        item: {
            marginHorizontal: -marginSize,
        },
    });
};

export default WorkerQualificationsScreen;
